import { useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  acceptInvitation,
  describeInvitation,
  type InvitationSummary,
  type Me,
} from '../api.js';

import { BrandMark, PRODUCT_LINE } from '../brand.js';

/**
 * Redeeming an invitation. The one screen that works without a session.
 *
 * The email field is deliberately EMPTY and not pre-filled. The address is what
 * binds the link to a person — `acceptInvitation` refuses if it does not match —
 * so showing it would let anyone the link was forwarded to read it off the page
 * and type it back in, defeating the only protection a forwarded link has.
 *
 * IT ALSO WARNS SOMEBODY WHO IS ALREADY SIGNED IN. Joining replaces the session
 * cookie, and a cookie belongs to the browser rather than to a tab, so the
 * account that was there is gone from every tab at once. The owner walked into
 * this while testing: he opened the invitation he had just sent, joined as the
 * site engineer, and read his own app through that person's eyes for twenty
 * minutes wondering where his projects had gone. The screen never said a word,
 * and the cost of saying it is three lines.
 */

interface Props {
  readonly token: string;
  readonly onJoined: () => void;
  /** Whoever this browser is currently signed in as, or null. */
  readonly signedInAs: Me | null;
}

type State =
  | { readonly status: 'checking' }
  | { readonly status: 'ready'; readonly invitation: InvitationSummary }
  | { readonly status: 'dead' };

export function AcceptInvite({ token, onJoined, signedInAs }: Props): JSX.Element {
  const [state, setState] = useState<State>({ status: 'checking' });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const invitation = await describeInvitation(token);
        if (!cancelled) setState({ status: 'ready', invitation });
      } catch {
        // Used, cancelled, expired and never-existed all look the same here,
        // because the server deliberately does not tell them apart.
        if (!cancelled) setState({ status: 'dead' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation({ token, email: email.trim(), name: name.trim(), password });
      onJoined();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not accept the invitation.');
      setBusy(false);
    }
  }

  if (state.status === 'checking') {
    return (
      <main className="auth-shell">
        <p aria-live="polite">Checking the link…</p>
      </main>
    );
  }

  if (state.status === 'dead') {
    return (
      <main className="auth-shell">
        <div className="card">
          <h1 className="brand">
          <BrandMark size={30} />
          {PRODUCT_LINE}
        </h1>
          <p className="brand-sub">This invitation link no longer works</p>
          <p>
            It may have already been used, been cancelled, or run out of time. Ask whoever
            invited you to send a new one.
          </p>
          <div className="button-row">
            <a href="/">Go to sign in</a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-shell">
      <form className="card" onSubmit={submit} noValidate>
        <h1 className="brand">
          <BrandMark size={30} />
          {PRODUCT_LINE}
        </h1>
        <p className="brand-sub">Join {state.invitation.company_name}</p>

        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}

        {signedInAs && (
          <div className="alert is-caution" role="status">
            <span>
              This browser is already signed in
              {signedInAs.company_name ? ` to ${signedInAs.company_name}` : ''}. Joining here
              signs that account out — in every tab, not just this one. To keep both, open this
              invitation in a private window instead.
            </span>
          </div>
        )}

        <div className="field">
          <label htmlFor="accept-email">Your email address</label>
          <input
            id="accept-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
            disabled={busy}
            autoComplete="email"
          />
          <p className="hint">The address the invitation was sent to.</p>
        </div>

        <div className="field">
          <label htmlFor="accept-name">Your name</label>
          <input
            id="accept-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            disabled={busy}
            autoComplete="name"
          />
        </div>

        <div className="field">
          <label htmlFor="accept-password">Choose a password</label>
          <input
            id="accept-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            maxLength={1024}
            disabled={busy}
            autoComplete="new-password"
          />
          <p className="hint">
            At least 12 characters. A short sentence you will remember is better than a short
            password you will not.
          </p>
        </div>

        <button type="submit" disabled={busy || !email.trim() || !name.trim() || !password}>
          {busy ? 'Joining…' : 'Join'}
        </button>
      </form>
    </main>
  );
}
